import { useEffect, useState } from "react";

/**
 * Start times for in-flight tool runs, keyed by `${messageId}-${partIndex}`.
 * Module-level so a remount — e.g. Equipment AI's tab rail unmounting the
 * chat pane while the user reads Sources — resumes the true elapsed time
 * instead of restarting at 0s. Entries are deleted when the run completes,
 * so the map only ever holds currently running tools.
 */
const runStartTimes = new Map<string, number>();

/**
 * Seconds a tool run has been in flight. With a `runKey`, the start time
 * survives unmount/remount; without one it falls back to the old
 * per-mount behavior (starts counting at mount).
 */
export const useToolRunElapsed = (
  runKey: string | undefined,
  isLoading: boolean,
): number => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isLoading) {
      if (runKey) runStartTimes.delete(runKey);
      return;
    }
    let start: number;
    if (runKey) {
      const existing = runStartTimes.get(runKey);
      start = existing ?? Date.now();
      if (existing === undefined) runStartTimes.set(runKey, start);
    } else {
      start = Date.now();
    }
    setElapsed(Math.floor((Date.now() - start) / 1000));
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [isLoading, runKey]);

  return elapsed;
};
